import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import redditApi from '../../../services/redditApi';
import RedditorBucketMatrix from './RedditorBucketMatrix';
import ArchiveToggle from '../components/ArchiveToggle';

function rateColor(rate) {
  if (rate <= 10) return '#16a34a';
  if (rate <= 20) return '#ca8a04';
  return '#dc2626';
}

const POLL_INTERVAL_MS = 15_000;

export default function RedditorPerformanceTab({ stats, loading }) {
  const navigate = useNavigate();

  // "Show deactivated redditors" — off by default, matching the Account
  // Performance "Show archived" pattern. When on, fetch a snapshot that
  // includes deactivated redditors instead of relying on the parent's
  // (always-active-only) polling.
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [deactivatedStats, setDeactivatedStats] = useState(null);
  const [loadingDeactivated, setLoadingDeactivated] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!showDeactivated) {
      clearInterval(pollRef.current);
      return;
    }
    function fetchIncludingDeactivated() {
      setLoadingDeactivated((prev) => (deactivatedStats ? prev : true));
      redditApi.get('/admin/stats/?include_deactivated=true')
        .then(({ data }) => setDeactivatedStats(data))
        .catch(() => {})
        .finally(() => setLoadingDeactivated(false));
    }
    fetchIncludingDeactivated();
    pollRef.current = setInterval(fetchIncludingDeactivated, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeactivated]);

  const activeStats  = showDeactivated ? deactivatedStats : stats;
  const isLoading     = showDeactivated ? (loadingDeactivated && !deactivatedStats) : loading;
  const redditors     = activeStats?.redditors || [];

  return (
    <div className="rh-tab-scroll">
      <div className="rh-page-header">
        <h2 className="rh-page-title">Redditor Performance</h2>
        <p className="rh-page-subtitle">Success rate, deletion rate, and activity per commenter</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <ArchiveToggle
          checked={showDeactivated}
          onChange={setShowDeactivated}
          onLabel="Showing deactivated"
          offLabel="Show deactivated"
          title="Deactivated redditors are hidden by default"
        />
      </div>

      <div className="rh-card">
        <div className="rh-table-wrap rh-table-scroll">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Redditor</th>
                <th>Reddit Accounts</th>
                <th>Assigned</th>
                <th>Submitted</th>
                <th>Deleted</th>
                <th>Scheduled</th>
                <th>Deletion Rate</th>
                <th>Overdue Now</th>
                <th>Total Overdue Incidents</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="rh-empty">Loading…</td></tr>
              ) : redditors.length === 0 ? (
                <tr><td colSpan={9} className="rh-empty">No redditors found.</td></tr>
              ) : redditors.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <button
                        onClick={() => navigate(`/reddit/admin/redditors/${r.id}`)}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          color: 'var(--rh-orange)', fontWeight: 600, fontSize: 13, textDecoration: 'underline',
                        }}
                      >
                        {r.name}
                      </button>
                      {r.is_active === false && (
                        <span className="rh-badge rh-badge-archived" style={{ fontSize: '0.68rem' }}>
                          Deactivated
                        </span>
                      )}
                    </span>
                  </td>
                  <td style={{ color: r.account_count > 0 ? 'var(--rh-text)' : 'var(--rh-text-muted)', fontWeight: r.account_count > 0 ? 600 : 400 }}>
                    {r.account_count ?? 0}
                  </td>
                  <td>{r.assigned}</td>
                  <td style={{ color: '#16a34a', fontWeight: 500 }}>{r.submitted}</td>
                  <td style={{ color: '#dc2626' }}>{r.deleted}</td>
                  <td style={{ color: '#ca8a04' }}>{r.scheduled}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: rateColor(r.deletion_rate) }}>
                      {r.deletion_rate}%
                    </span>
                  </td>
                  <td>
                    {(r.overdue_count ?? 0) > 0 ? (
                      <span style={{
                        fontWeight: 700, fontSize: 13,
                        color: '#c2410c', background: '#fff7ed',
                        borderRadius: 6, padding: '2px 8px',
                      }}>
                        {r.overdue_count}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--rh-text-muted)', fontSize: 13 }}>0</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const n = r.total_overdue_incidents ?? 0;
                      const color = n >= 10 ? '#b91c1c' : n >= 5 ? '#c2410c' : '#6b7280';
                      const bg = n >= 10 ? '#fef2f2' : n >= 5 ? '#fff7ed' : 'transparent';
                      return (
                        <span style={{
                          fontWeight: n > 0 ? 600 : 400, fontSize: 13,
                          color, background: bg,
                          borderRadius: 6, padding: n > 0 ? '2px 8px' : '0',
                        }}>
                          {n}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deletion rate bar chart (CSS-based) */}
      {!isLoading && redditors.length > 0 && (
        <div className="rh-card" style={{ padding: '20px 24px' }}>
          <h3 className="rh-card-title" style={{ marginBottom: 20 }}>Deletion Rate by Redditor</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {redditors.map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 200, fontSize: 13, color: 'var(--rh-text)', fontWeight: 500, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name}
                </span>
                <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 6, height: 16, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.min(100, r.deletion_rate)}%`,
                      height: '100%',
                      background: rateColor(r.deletion_rate),
                      borderRadius: 6,
                      transition: 'width 0.6s ease',
                    }}
                  />
                </div>
                <span style={{ width: 42, fontSize: 13, fontWeight: 600, color: rateColor(r.deletion_rate), textAlign: 'right', flexShrink: 0 }}>
                  {r.deletion_rate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <RedditorBucketMatrix
        title="Deletion Rate Analysis"
        views={[
          {
            key: 'karma',
            label: 'Karma',
            subtitle: 'Deletion rates by account comment-karma, cross-referenced against each Redditor',
            endpoint: '/admin/account-metric-stats/?dimension=karma',
            allLabel: 'All Karma',
            csvPrefix: 'Karma_Analysis',
            footnote: "Karma = the posting account's comment karma at its last quality check (hypothesis threshold: 1,000). Only comments with a known posting account are counted; Unknown = account never quality-checked.",
          },
          {
            key: 'post_age',
            label: 'Post Age',
            subtitle: 'Deletion rates by post age, cross-referenced against each Redditor',
            endpoint: '/admin/post-age-stats/',
            allLabel: 'All Ages',
            csvPrefix: 'Post_Age_Analysis',
            footnote: 'Post age = age of the Reddit post at time of submission.',
          },
          {
            key: 'account_age',
            label: 'Account Age',
            subtitle: 'Deletion rates by account age, cross-referenced against each Redditor',
            endpoint: '/admin/account-metric-stats/?dimension=age',
            allLabel: 'All Ages',
            csvPrefix: 'Account_Age_Analysis',
            footnote: "Account age computed from the posting account's creation date (hypothesis threshold: 365 days / 1 year). Only comments with a known posting account are counted; Unknown = account never quality-checked.",
          },
          {
            key: 'comment_length',
            label: 'Comment Length',
            subtitle: 'Deletion rates by submitted comment character length, cross-referenced against each Redditor',
            endpoint: '/admin/comment-length-stats/',
            allLabel: 'All Lengths',
            csvPrefix: 'Comment_Length_Analysis',
            detailExport: '/admin/comment-length-stats/export/',
            footnote: 'Length is counted from the comment text at submit time. Rates use LIVE + DELETED only; Submitted (unchecked) and DWS are listed separately as pending/unverified.',
          },
        ]}
      />
    </div>
  );
}
